import { memo, ReactElement, ReactNode } from "react";
import { FlatList, FlatListProps, View } from "react-native";

import { NestedNode } from "./types";

/**
 * Exclude props that this component is supposed to provide.
 */
export type TrimmedFlatListProps = Omit<
  FlatListProps<NestedNode>,
  "renderItem" | "getItem" | "data" | "getItemCount" | "ListEmptyComponent"
>;

export type NestedFlatNodeProps = TrimmedFlatListProps & {
  node: NestedNode;
  depth: number;
  // Recursive generic is hard :(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderNode: (node: any, depth: number) => ReactNode;
  // Recursive generic is hard :(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderChildrenContainer?: (node: any, children: ReactNode) => ReactNode;
  renderEmptyComponent?: (depth: number) => JSX.Element;
  listKey?: string;
};

function renderDefaultChildrenContainer(node: NestedNode, children: ReactNode) {
  return node.isOpen ? <View>{children}</View> : null;
}

function NestedFlatNode(props: NestedFlatNodeProps): ReactElement<NestedFlatNodeProps> {
  const {
    node,
    depth,
    renderNode,
    renderChildrenContainer = renderDefaultChildrenContainer,
    renderEmptyComponent,
    ...passThroughProps
  } = props;

  const children = (
    <FlatList
      {...passThroughProps}
      data={node.children}
      renderItem={(itemInfo) => (
        <NestedFlatNode
          node={itemInfo.item}
          depth={depth + 1}
          listKey={`${props.listKey}.${itemInfo.index + 1}`}
          renderNode={renderNode}
          renderChildrenContainer={renderChildrenContainer}
        />
      )}
      ListEmptyComponent={renderEmptyComponent ? renderEmptyComponent(depth) : undefined}
    />
  );

  return (
    <>
      {renderNode(node, depth)}
      {renderChildrenContainer(node, children)}
    </>
  );
}

export default memo(NestedFlatNode) as typeof NestedFlatNode;
